import { ref, computed } from "vue";
import { db, auth } from "@/firebase";
import { onAuthStateChanged } from "firebase/auth";
import {
  collection,
  onSnapshot,
  query,
  where,
  orderBy,
  doc,
  getDoc,
  getDocs,
  updateDoc,
  addDoc,
  deleteDoc,
  serverTimestamp,
} from "firebase/firestore";
import type { SessionData } from "@/services/sessionService";
import { checkUsersExist, sanitizeUserDisplay, validateSessionUsers } from "@/utils/sessionCleanup";

// Interface for transformed session data with display properties
interface TransformedSessionData {
  id: string;
  studentName: string;
  studentEmail: string;
  tutorName: string;
  status: "pending" | "confirmed" | "in-progress" | "completed" | "cancelled" | "declined";
  type: "face-to-face" | "online";
  date: string;
  time: string;
  subject: string;
  notes: string;
  location?: string;
  meetingLink?: string;
  calendarEventId?: string;
  startAt: any;
  endAt?: any;
  tutorId?: string;
  studentId?: string;
  studentProfileImage?: string | null;
  // Session materials uploaded by student
  materials?: Array<{
    name: string;
    url: string;
    size: number;
    type: string;
    uploadedAt?: Date;
    uploadedBy?: string;
  }>;
  // Additional properties for completed sessions
  yourRating?: string;
  studentRating?: string;
  feedback?: string;
  feedbackStatus: "given" | "pending";
}

// Global reactive state - shared across components
const pendingSessions = ref<TransformedSessionData[]>([]);
const confirmedSessions = ref<TransformedSessionData[]>([]);
const completedSessions = ref<TransformedSessionData[]>([]);
const cancelledSessions = ref<TransformedSessionData[]>([]);
const isLoadingSessions = ref(true);
const studentCache = ref<{ [key: string]: any }>({});
const currentTutorData = ref<any>(null);
let unsubscribe: (() => void) | null = null;
let authUnsubscribe: (() => void) | null = null;
let lastFetchTime = 0;

// Helper function to fetch ratings for a specific session
const fetchRatingsForSession = async (sessionId: string, currentUserId: string) => {
  try {
    // Query feedbacks collection for this session
    const feedbacksRef = collection(db, "feedbacks");
    const q = query(feedbacksRef, where("sessionID", "==", sessionId));

    const querySnapshot = await getDocs(q);

    let yourRating = "0";
    let studentRating = "0";

    querySnapshot.forEach((doc) => {
      const feedbackData = doc.data();

      // Rating given by current user (tutor)
      if (feedbackData.fromUserID === currentUserId) {
        yourRating = feedbackData.overallScore?.toString() || "0";
      }

      // Rating given to current user (tutor) by student
      if (feedbackData.toUserID === currentUserId) {
        studentRating = feedbackData.overallScore?.toString() || "0";
      }
    });

    return { yourRating, studentRating };
  } catch (error) {
    // Silent fail - return default ratings
    return { yourRating: "0", studentRating: "0" };
  }
};
let loadingTimeout: ReturnType<typeof setTimeout> | null = null;
const CACHE_DURATION = 30000; // 30 seconds

// Get current authenticated user ID
const getCurrentUserId = () => {
  return auth.currentUser?.uid || null;
};

// Batch fetch all user data to avoid multiple sequential requests
const batchFetchUserData = async (userIds: string[]) => {
  const now = Date.now();

  // Check if we have recent data in cache
  if (now - lastFetchTime < CACHE_DURATION && Object.keys(studentCache.value).length > 0) {
    return studentCache.value;
  }

  const uniqueUserIds = [...new Set(userIds)].filter(Boolean);
  const uncachedIds = uniqueUserIds.filter((id) => !studentCache.value[id]);

  if (uncachedIds.length === 0) {
    return studentCache.value;
  }

  const userPromises = uncachedIds.map(async (userId) => {
    try {
      const userDocRef = doc(db, "users", userId);
      const userDoc = await getDoc(userDocRef);

      if (userDoc.exists()) {
        const userData = userDoc.data();
        studentCache.value[userId] = userData;
      }
    } catch (error) {
      // Silent fail - cache remains empty for this user
    }
  });

  await Promise.all(userPromises);
  lastFetchTime = now;
  return studentCache.value;
};

// Transform Firebase session data to component expected format
const transformSessionData = async (session: any) => {
  const startDate = session.startAt.toDate();
  const endDate = session.endAt.toDate();

  // Fetch student data from cache or Firestore
  const studentData = await batchFetchUserData([session.studentId]).then(
    (data) => data[session.studentId]
  );
  
  // Check if student exists and validate session if needed
  const studentExists = !!studentData;
  if (!studentExists && ["pending", "confirmed", "in-progress"].includes(session.sessionStatus)) {
    // Auto-cancel session if student doesn't exist
    await validateSessionUsers(session.id, session.tutorId, session.studentId);
  }
  
  let studentName = "Student";
  const studentEmail = studentData?.email || "No email";

  if (studentData && studentExists) {
    const firstName = studentData.firstName;
    const lastName = studentData.lastName;

    if (firstName && lastName) {
      studentName = `${firstName} ${lastName}`;
    } else if (firstName) {
      studentName = firstName;
    } else if (lastName) {
      studentName = lastName;
    } else if (studentData.displayName) {
      studentName = studentData.displayName;
    } else if (studentData.email) {
      studentName = studentData.email.split('@')[0];
    }
  } else {
    studentName = "Deactivated User";
  }

  // Fetch tutor data for tutor name
  let tutorName = "Tutor";
  const tutorData = await batchFetchUserData([session.tutorId]).then(
    (data) => data[session.tutorId]
  );

  // Check if tutor exists
  const tutorExists = !!tutorData;
  if (!tutorExists && ["pending", "confirmed", "in-progress"].includes(session.sessionStatus)) {
    // Auto-cancel session if tutor doesn't exist
    await validateSessionUsers(session.id, session.tutorId, session.studentId);
  }

  if (tutorData && tutorExists) {
    const firstName = tutorData.firstName;
    const lastName = tutorData.lastName;

    if (firstName && lastName) {
      tutorName = `${firstName} ${lastName}`;
    } else if (firstName) {
      tutorName = firstName;
    } else if (lastName) {
      tutorName = lastName;
    } else if (tutorData.displayName) {
      tutorName = tutorData.displayName;
    }
  } else {
    tutorName = "Deactivated User";
  }

  // Determine feedback status for current user
  const currentUserId = getCurrentUserId();
  let feedbackStatus: "given" | "pending" = "pending";
  if (currentUserId && session[`feedbackStatus_${currentUserId}`] === "given") {
    feedbackStatus = "given";
  }

  // Fetch actual ratings from feedbacks collection for completed sessions
  let ratings = { yourRating: "0", studentRating: "0" };
  let feedbacks: any[] = [];
  if (currentUserId && session.sessionStatus === "completed") {
    ratings = await fetchRatingsForSession(session.id, currentUserId);
    
    // Fetch all feedbacks for this session from top-level feedbacks collection
    try {
      const feedbacksQuery = query(
        collection(db, "feedbacks"),
        where("sessionID", "==", session.id)
      );
      const feedbacksSnapshot = await getDocs(feedbacksQuery);
      feedbacks = feedbacksSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      // Silently fail - feedbacks are optional
    }
  }

  return {
    id: session.id,
    studentName: studentName,
    studentEmail: studentEmail,
    studentProfileImage: studentData?.profileImage || null,
    tutorName: tutorName,
    status: session.sessionStatus,
    type: (session.mode.toLowerCase() === "face-to-face" ? "face-to-face" : "online") as
      | "face-to-face"
      | "online",
    date: startDate.toLocaleDateString("en-US", {
      weekday: "short",
      year: "numeric",
      month: "short",
      day: "numeric",
    }),
    time: `${startDate.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    })} - ${endDate.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    })}`,
    subject: session.subject,
    notes: session.notes,
    location: session.location,
    meetingLink: session.meetingLink,
    calendarEventId: session.calendarEventId,
    startAt: session.startAt,
    endAt: session.endAt,
    tutorId: session.tutorId,
    studentId: session.studentId,
    // Include materials with proper date conversion
    materials: session.materials?.map((material: any) => ({
      ...material,
      uploadedAt: material.uploadedAt?.toDate ? material.uploadedAt.toDate() : material.uploadedAt
    })) || [],
    yourRating: ratings.yourRating,
    studentRating: ratings.studentRating,
    feedback: session.feedback || "",
    feedbacks: feedbacks, // Include all feedbacks for the session
    feedbackStatus: feedbackStatus,
  };
};

// Helper function to process sessions asynchronously
const processSessionsAsync = async (
  allUserIds: string[],
  pendingFiltered: any[],
  confirmedFiltered: any[],
  completedFiltered: any[],
  cancelledFiltered: any[]
) => {
  try {
    // First, fetch all user data (students and tutors) before transforming sessions
    if (allUserIds.length > 0) {
      await batchFetchUserData(allUserIds);
    }

    // Now transform sessions with proper student names and ratings
    const finalPendingTransformed = await Promise.all(pendingFiltered.map(transformSessionData));
    const finalConfirmedTransformed = await Promise.all(
      confirmedFiltered.map(transformSessionData)
    );
    const finalCompletedTransformed = await Promise.all(
      completedFiltered.map(transformSessionData)
    );
    const finalCancelledTransformed = await Promise.all(
      cancelledFiltered.map(transformSessionData)
    );

    // Update UI with complete data
    pendingSessions.value = finalPendingTransformed.sort(
      (a: any, b: any) =>
        new Date(a.startAt.toDate()).getTime() - new Date(b.startAt.toDate()).getTime()
    );

    confirmedSessions.value = finalConfirmedTransformed.sort(
      (a: any, b: any) =>
        new Date(a.startAt.toDate()).getTime() - new Date(b.startAt.toDate()).getTime()
    );

    completedSessions.value = finalCompletedTransformed.sort(
      (a: any, b: any) =>
        new Date(b.startAt.toDate()).getTime() - new Date(a.startAt.toDate()).getTime()
    );

    cancelledSessions.value = finalCancelledTransformed.sort(
      (a: any, b: any) =>
        new Date(b.startAt.toDate()).getTime() - new Date(a.startAt.toDate()).getTime()
    );
  } catch (error) {
    isLoadingSessions.value = false;
  }
};

// New function that waits for authentication
const initializeSessionListenerWithAuth = () => {
  if (unsubscribe) {
    return; // Already initialized
  }

  // Set loading to true immediately
  isLoadingSessions.value = true;

  // Cancel any existing auth subscription
  if (authUnsubscribe) {
    authUnsubscribe();
  }

  // Wait for authentication
  authUnsubscribe = onAuthStateChanged(auth, (user) => {
    if (user) {
      initializeSessionListener(user.uid);
    } else {
      isLoadingSessions.value = false;
      pendingSessions.value = [];
      confirmedSessions.value = [];
      completedSessions.value = [];
    }
  });
};

const initializeSessionListener = async (userId?: string) => {
  if (unsubscribe) {
    return; // Already initialized
  }

  const currentUserId = userId || getCurrentUserId();
  if (!currentUserId) {
    // Don't initialize if user is not authenticated yet
    // Set a short timeout to retry until user is authenticated
    setTimeout(() => initializeSessionListenerWithAuth(), 500);
    return;
  }

  // Set loading to true immediately
  isLoadingSessions.value = true;

  // Fetch current tutor's data to get their averageRating
  try {
    const tutorDoc = await getDoc(doc(db, "users", currentUserId));
    if (tutorDoc.exists()) {
      currentTutorData.value = tutorDoc.data();
    }
  } catch (error) {
    // Silent fail - will use calculated rating as fallback
  }

  // Set a timeout to prevent infinite loading
  loadingTimeout = setTimeout(() => {
    if (isLoadingSessions.value) {
      isLoadingSessions.value = false;
    }
  }, 5000); // 5 second timeout

  const sessionsRef = collection(db, "sessions");
  const sessionsQuery = query(
    sessionsRef,
    where("tutorId", "==", currentUserId),
    where("status", "==", "active")
  );

  unsubscribe = onSnapshot(sessionsQuery, async (snapshot) => {
    try {
      const allSessions = snapshot.docs.map((doc) => {
        return {
          id: doc.id,
          ...doc.data(),
        };
      });

      // Process data synchronously first
      const pendingFiltered = allSessions.filter(
        (session: any) => session.sessionStatus === "pending"
      );

      const confirmedFiltered = allSessions.filter(
        (session: any) => session.sessionStatus === "confirmed" || session.sessionStatus === "in-progress"
      );

      const completedFiltered = allSessions.filter(
        (session: any) => session.sessionStatus === "completed"
      );

      const cancelledFiltered = allSessions.filter(
        (session: any) => session.sessionStatus === "cancelled" || session.sessionStatus === "declined"
      );

      // Extract unique user IDs (both students and tutors) for batch fetching
      const allStudentIds = allSessions.map((session: any) => session.studentId).filter(Boolean);
      const allTutorIds = allSessions.map((session: any) => session.tutorId).filter(Boolean);
      const allUserIds = [...new Set([...allStudentIds, ...allTutorIds])];

      // Process sessions asynchronously and wait for completion
      await processSessionsAsync(allUserIds, pendingFiltered, confirmedFiltered, completedFiltered, cancelledFiltered);

      // Clear the loading timeout
      if (loadingTimeout) {
        clearTimeout(loadingTimeout);
      }

      // Set loading to false only after all data is processed
      isLoadingSessions.value = false;
    } catch (error) {
      if (loadingTimeout) {
        clearTimeout(loadingTimeout);
      }
      isLoadingSessions.value = false;
    }
  });
};

// Update session status
export const updateSessionStatus = async (
  sessionId: string,
  sessionStatus: "pending" | "confirmed" | "in-progress" | "completed" | "cancelled" | "declined",
  cancellationReason?: string,
  cancellationNotes?: string
): Promise<boolean> => {
  try {
    const sessionRef = doc(db, "sessions", sessionId);
    const updateData: any = {
      sessionStatus,
      updatedAt: new Date(),
    };

    // Add cancellation reason and notes if provided
    if (cancellationReason) {
      updateData.cancellationReason = cancellationReason;
    }
    if (cancellationNotes) {
      updateData.cancellationNotes = cancellationNotes;
    }

    await updateDoc(sessionRef, updateData);

    return true;
  } catch (error) {
    return false;
  }
};

// Update session meeting link and calendar event ID
export const updateSessionMeetingLink = async (
  sessionId: string,
  meetingLink: string,
  calendarEventId?: string
): Promise<boolean> => {
  try {
    const sessionRef = doc(db, "sessions", sessionId);
    const updateData: any = {
      meetingLink,
      updatedAt: new Date(),
    };

    if (calendarEventId) {
      updateData.calendarEventId = calendarEventId;
    }

    await updateDoc(sessionRef, updateData);

    return true;
  } catch (error) {
    return false;
  }
};

// Delete Google Calendar event when session is cancelled
export const deleteSessionCalendarEvent = async (
  sessionId: string,
  calendarEventId: string,
  userId: string
): Promise<boolean> => {
  try {
    // Import the delete function dynamically to avoid circular imports
    const { deleteGoogleCalendarEvent } = await import("@/services/googleCalendarApi");

    const success = await deleteGoogleCalendarEvent(userId, calendarEventId);

    if (success) {
      // Remove the calendar event ID from the session document
      const sessionRef = doc(db, "sessions", sessionId);
      await updateDoc(sessionRef, {
        calendarEventId: null,
        updatedAt: new Date(),
      });
    }

    return success;
  } catch (error) {
    return false;
  }
};

// Add session to blocked slots when student books (pending)
export const addToBlockedSlots = async (
  tutorId: string,
  sessionId: string,
  startTime: string,
  endTime: string,
  sessionData: {
    studentId: string;
    studentName: string;
    subject: string;
    type: "face-to-face" | "online";
    location?: string;
    notes?: string;
  }
): Promise<boolean> => {
  try {
    const blockedSlotsRef = collection(db, "users", tutorId, "blockedSlots");

    const docRef = await addDoc(blockedSlotsRef, {
      sessionId,
      startTime,
      endTime,
      studentId: sessionData.studentId,
      studentName: sessionData.studentName,
      subject: sessionData.subject,
      type: sessionData.type,
      location: sessionData.location || null,
      notes: sessionData.notes || null,
      status: "pending",
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });

    return true;
  } catch (error) {
    return false;
  }
};

// Remove session from blocked slots when cancelled
export const removeFromBlockedSlots = async (
  tutorId: string,
  sessionId: string
): Promise<boolean> => {
  try {
    // Query to find the blocked slot document
    const blockedSlotsRef = collection(db, "users", tutorId, "blockedSlots");
    const q = query(blockedSlotsRef, where("sessionId", "==", sessionId));

    // Get the document
    const querySnapshot = await getDocs(q);

    if (querySnapshot.empty) {
      return true; // Not an error if no blocked slot exists
    }

    // Delete the blocked slot document
    const blockedSlotDoc = querySnapshot.docs[0];
    await deleteDoc(blockedSlotDoc.ref);

    return true;
  } catch (error) {
    return false;
  }
};

// Get blocked slots for a tutor (for availability checking)
export const getBlockedSlots = async (
  tutorId: string,
  startDate: string,
  endDate: string
): Promise<any[]> => {
  try {
    const blockedSlotsRef = collection(db, "users", tutorId, "blockedSlots");
    const q = query(
      blockedSlotsRef,
      where("startTime", ">=", startDate),
      where("startTime", "<=", endDate),
      orderBy("startTime")
    );

    const querySnapshot = await getDocs(q);
    const blockedSlots = querySnapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));

    return blockedSlots;
  } catch (error) {
    return [];
  }
};

// Test function to verify blocked slots creation
export const testBlockedSlots = async (tutorId: string) => {
  try {
    const blockedSlotsRef = collection(db, "users", tutorId, "blockedSlots");
    const q = query(blockedSlotsRef);
    const querySnapshot = await getDocs(q);

    return querySnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    return [];
  }
};

// Computed properties for summary cards
const totalUpcomingSessions = computed(() => {
  return pendingSessions.value.length + confirmedSessions.value.length;
});

const totalCompletedSessions = computed(() => {
  return completedSessions.value.length;
});

const totalCancelledSessions = computed(() => {
  return cancelledSessions.value.length;
});

const averageRating = computed(() => {
  // ✅ Use pre-calculated average rating from user document (INSTANT!)
  if (currentTutorData.value?.averageRating !== undefined) {
    const rating = currentTutorData.value.averageRating;
    return typeof rating === 'number' ? rating.toFixed(1) : rating.toString();
  }

  // Fallback: calculate from completed sessions if user data not loaded yet
  if (completedSessions.value.length === 0) return "0.0";

  const ratingsReceived = completedSessions.value
    .map((session) => parseFloat(session.studentRating || "0"))
    .filter((rating) => rating > 0);

  if (ratingsReceived.length === 0) return "0.0";

  const totalRating = ratingsReceived.reduce((sum, rating) => sum + rating, 0);
  return (totalRating / ratingsReceived.length).toFixed(1);
});

const totalHours = computed(() => {
  if (completedSessions.value.length === 0) return "0";

  const totalMinutes = completedSessions.value.reduce((sum, session) => {
    // Calculate actual session duration in minutes
    const startTime = session.startAt?.toDate ? session.startAt.toDate() : new Date(session.startAt);
    const endTime = session.endAt ? (session.endAt.toDate ? session.endAt.toDate() : new Date(session.endAt)) : new Date(startTime.getTime() + 60 * 60 * 1000);

    // Calculate the difference in milliseconds and convert to minutes
    const durationMs = endTime - startTime;
    const durationMinutes = Math.round(durationMs / (1000 * 60));

    // Only count positive durations
    return durationMinutes > 0 ? sum + durationMinutes : sum;
  }, 0);

  // Never show negative hours
  return Math.max(0, Math.round(totalMinutes / 60)).toString();
});

// Clear cache function for debugging/refresh
export const clearTutorSessionCache = () => {
  studentCache.value = {};
  lastFetchTime = 0;
  pendingSessions.value = [];
  confirmedSessions.value = [];
  completedSessions.value = [];
  isLoadingSessions.value = true;
  if (loadingTimeout) {
    clearTimeout(loadingTimeout);
    loadingTimeout = null;
  }
  if (unsubscribe) {
    unsubscribe();
    unsubscribe = null;
  }
  if (authUnsubscribe) {
    authUnsubscribe();
    authUnsubscribe = null;
  }
};

export function useTutorSessionStore() {
  // Initialize on first use - ensure we try to initialize even if a previous attempt didn't succeed
  const userId = getCurrentUserId();

  if (userId && !unsubscribe) {
    initializeSessionListener(userId);
  } else if (!unsubscribe) {
    initializeSessionListenerWithAuth();
  }

  return {
    pendingSessions,
    confirmedSessions,
    completedSessions,
    cancelledSessions,
    isLoadingSessions,
    totalCompletedSessions,
    totalCancelledSessions,
    totalUpcomingSessions,
    averageRating,
    totalHours,
    updateSessionStatus,
    updateSessionMeetingLink,
    deleteSessionCalendarEvent,
    addToBlockedSlots,
    removeFromBlockedSlots,
    getBlockedSlots,
    clearTutorSessionCache,
    // Export the initialize function to allow manual initialization from components
    initializeListener: () => {
      if (unsubscribe) {
        // Clear existing subscription first
        unsubscribe();
        unsubscribe = null;
      }
      initializeSessionListenerWithAuth();
    },
  };
}
